<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div
                v-show="activity"
                class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50"
            >
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="activity"
                        class="fixed inset-0 transform transition-all"
                        @click="cancel"
                    >
                        <div class="absolute inset-0 bg-gray-500 opacity-75" />
                    </div>
                </transition>

                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="activity"
                        class="shadow-xl transform transition-all mx-10"
                    >
                        <main class="bg-white rounded-lg">
                            <div v-if="activity" class="w-full h-full flex flex-col">
                                <div class="flex w-full">
                                    <div class="w-1/2 flex flex-col p-10 space-y-4">

                                        <editable-input-field @saved="updateActivity" v-model="activity.name" size="l" />
                                        <editable-text-area @saved="updateActivity" class="my-6" v-model="activity.description" />

                                        <div v-if="activity.place" class="relative w-full h-96">
                                            <GMapMap
                                                :center="{
                                                lat: activity.place.location.coordinates[1],
                                                lng: activity.place.location.coordinates[0]
                                            }"
                                                :zoom="12"
                                                map-type-id="terrain"
                                                :options="{
                                                  zoomControl: false,
                                                  mapTypeControl: false,
                                                  scaleControl: false,
                                                  streetViewControl: false,
                                                  rotateControl: false,
                                                  fullscreenControl: false,
                                            }"
                                                style="position: absolute; width: 100%; height: 100%; border-radius: 6px"
                                            >
                                                <GMapMarker
                                                    :position="{
                                                    lat: activity.place.location.coordinates[1],
                                                    lng: activity.place.location.coordinates[0]
                                                }"
                                                />
                                            </GMapMap>
                                        </div>
                                      <div v-else-if="activity.location" class="relative w-full h-96">
                                        <GMapMap
                                            :center="{
                                                lat: activity.location.coordinates[1],
                                                lng: activity.location.coordinates[0]
                                            }"
                                            :zoom="12"
                                            map-type-id="terrain"
                                            :options="{
                                                  zoomControl: false,
                                                  mapTypeControl: false,
                                                  scaleControl: false,
                                                  streetViewControl: false,
                                                  rotateControl: false,
                                                  fullscreenControl: false,
                                            }"
                                            style="position: absolute; width: 100%; height: 100%; border-radius: 6px"
                                        >
                                          <GMapMarker
                                              :position="{
                                                    lat: activity.location.coordinates[1],
                                                    lng: activity.location.coordinates[0]
                                                }"
                                          />
                                        </GMapMap>
                                      </div>
                                      <div v-else-if="activity.destination" class="relative w-full h-96">
                                        <GMapMap
                                            :center="{
                                                lat: activity.destination.location.coordinates[1],
                                                lng: activity.destination.location.coordinates[0]
                                            }"
                                            :zoom="12"
                                            map-type-id="terrain"
                                            :options="{
                                                  zoomControl: false,
                                                  mapTypeControl: false,
                                                  scaleControl: false,
                                                  streetViewControl: false,
                                                  rotateControl: false,
                                                  fullscreenControl: false,
                                            }"
                                            style="position: absolute; width: 100%; height: 100%; border-radius: 6px"
                                        >
                                          <GMapMarker
                                              :position="{
                                                    lat: activity.destination.location.coordinates[1],
                                                    lng: activity.destination.location.coordinates[0]
                                                }"
                                          />
                                        </GMapMap>
                                      </div>
                                    </div>

                                    <div class="w-1/2 h-full flex flex-col justify-between p-10 rounded-r-lg">
                                        <ul role="list" class="grid w-full grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                                            <li v-for="file in activity.media" :key="file.id" class="relative">
                                                <div
                                                    @click="openCrop(file)"
                                                    class="group cursor-pointer relative aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 border border-2 border-transparent hover:border-primary"
                                                >
                                                  <img v-if="file.pivot?.media_crop" :src="file.pivot.media_crop.url" alt="" class="pointer-events-none object-cover group-hover:opacity-75" />
                                                  <img v-else :src="file.original_url" alt="" class="pointer-events-none object-cover group-hover:opacity-75" />
                                                  <div class="w-full h-full hidden group-hover:flex flex-col justify-center items-center">
                                                    <svg class="m-1 text-white h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                                    </svg>
                                                    <span class="font-semibold text-white text-sm">Crop</span>
                                                  </div>
                                                </div>
                                                <p class="pointer-events-none mt-1.5 block truncate text-xs font-medium text-font-dark">{{ file.name }}</p>
                                            </li>
                                        </ul>
                                        <media-picker-multiple
                                            :selected="activity.media"
                                            :settings="{
                                                activity: {
                                                    name: activity.name,
                                                    get: route('api.place.media.index', {place: activity.place_id}),
                                                    upload: route('api.proposal.destination.itinerary.media.store', {proposal: proposal.id, destination: destination.id, itinerary: activity.id})
                                                },
                                                proposal: {
                                                    name: 'Current Proposal',
                                                    get: route('api.proposal.media.index', {proposal: proposal.id}),
                                                    upload: route('api.proposal.media.store', {proposal: proposal.id})
                                                }
                                            }"
                                            :default_search="activity.name"
                                            @update="updateMedia"
                                        />
                                    </div>
                                </div>
                                <div class="flex w-full justify-end space-x-2 p-4">
                                    <button @click="deleteActivity" class="bg-red-100 text-red-800 rounded-md text-sm py-1 px-2 hover:bg-red-200">Remove activity</button>
                                    <button @click="cancel" class="bg-button-purple text-white rounded-md text-sm py-1 px-2 border hover:bg-primary">Done</button>
                                </div>
                            </div>

                          <MediaCropper
                              v-if="edit_file"
                              :image="edit_file"
                              @close="edit_file = null"
                              @crop="saveCrop"
                          />
                        </main>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import {ref, onMounted, onUnmounted, watch} from 'vue'
import Button from "@/Jetstream/Button";
import {useForm} from "@inertiajs/vue3";
import MediaPickerMultiple from "@/Components/Media/MediaPickerMultiple.vue";
import EditableTextArea from "@/ComponentsDeprecated/Proposal/EditableTextArea";
import EditableInputField from "@/ComponentsDeprecated/Proposal/EditableInputField";
import MediaCropper from "@/ComponentsDeprecated/MediaCropper";

onMounted(() => document.addEventListener('keydown', closeOnEscape));
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));

const selected = ref(null)
const edit_file = ref(null)

const emit = defineEmits({
    cancel: (_value) => true,
    delete: (_value) => true,
    updated: (_value) => true
});

const props = defineProps({
    activity: {
        type: Object,
        required: false,
        default: null,
    },
    proposal: {
        type: Object,
        required: true
    },
    destination: {
        type: Object,
        required: true
    }
})

const closeOnEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
        cancel();
    }
};

const updateMedia = (media) => {
    axios.post(route('api.proposal.destination.itinerary.media.sync', {proposal: props.proposal.id, destination: props.destination.id, itinerary: props.activity.id}), {
        images: media
    }).then(response => {
        props.activity.media = response.data.images
    }).catch(error => {
        console.log('error')
        console.log(error)
    }).finally(() => {
    })
}

const updateActivity = () => {
    axios.put(route('api.proposal.itinerary.update', {proposal: props.proposal.id, itinerary: props.activity.id}), props.activity)
        .then(() => {})
}

const deleteActivity = () => {
    emit('delete')
}

const cancel = () => {
    emit('cancel');
};

function openCrop(file) {
  edit_file.value = file
}

function saveCrop(res) {
  axios.post(route('api.proposal.destination.itinerary.media.crop', {proposal: props.proposal.id, destination: props.destination.id, itinerary: props.activity.id, media: edit_file.value.id}), {
    media_crop: res
  }).then(response => {
    props.activity.media[props.activity.media.indexOf(edit_file.value)].pivot['media_crop'] = response.data
  }).catch(error => {
    console.log('error')
    console.log(error)
  }).finally(() => {
    edit_file.value = null
  })
}

</script>
